/**
 * /admin/ops/errors — Error Incident Center (SCRUM-332).
 * Agrupa por fingerprint real (event + mensaje) en vez de listar cientos de
 * ocurrencias idénticas. KPIs + filtros + drill-down con muestras reales.
 *
 * Gaps documentados (no fabricados): sm_platform_errors no tiene columna de
 * "resuelto" ni un mecanismo de retry seguro genérico — no se agregaron
 * acciones de "marcar resuelto"/"reintentar" sin esa base real. Tampoco hay
 * correlation_id compartido con audit_log ni con sm_cron_runs — el único CTA
 * de correlación real es Ver traza (via request_id, ya existía) y Ver
 * workspace. El listado plano legacy (platform.api.exception vía
 * sm_platform_events) no entra en los KPIs/agrupación nuevos.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, SlidersHorizontal } from "lucide-react";
import { OpsSubnav } from "@/components/OpsSubnav";
import { TenantPicker, type TenantPickerOption } from "@/components/admin/TenantPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { apiGetJson } from "@/lib/sm-api";
import type { OpsErrorRow } from "@/lib/ops-types";

type ErrorGroup = {
  fingerprint: string;
  event: string;
  sampleMessage: string;
  severity: string;
  count: number;
  distinctTenants: number;
  firstSeenAt: string;
  lastSeenAt: string;
};

type Kpis = { total: number; critical: number; warn: number; distinctTenants: number; distinctFingerprints: number };

const SEVERITY_LABELS: Record<string, string> = { error: "Crítico", warn: "Warning" };
const SEVERITY_COLOR: Record<string, string> = {
  error: "border-red-500/40 bg-red-500/10 text-red-700",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-700",
};
const SINCE_PRESETS = [
  { value: "24h", label: "24 horas" },
  { value: "7d", label: "7 días" },
  { value: "30d", label: "30 días" },
] as const;

function presetToSince(preset: string): string {
  const ms = preset === "24h" ? 86_400_000 : preset === "7d" ? 7 * 86_400_000 : 30 * 86_400_000;
  return new Date(Date.now() - ms).toISOString();
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

export const Route = createFileRoute("/admin/ops/errors")({
  component: OpsErrorsPage,
  head: () => ({ meta: [{ title: "Errores — Operaciones" }] }),
  validateSearch: (s: Record<string, unknown>) => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
  }),
});

function KpiTile({ label, value, tone }: { label: string; value: number; tone?: "warn" | "error" }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold mt-0.5 ${tone === "error" && value > 0 ? "text-red-600" : tone === "warn" && value > 0 ? "text-amber-600" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function OpsErrorsPage() {
  const { q: qFromUrl } = Route.useSearch();
  const [tenants, setTenants] = useState<TenantPickerOption[]>([]);
  const [groups, setGroups] = useState<ErrorGroup[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [since, setSince] = useState("24h");
  const [severity, setSeverity] = useState("all");
  const [tenantId, setTenantId] = useState("");
  const [q, setQ] = useState(qFromUrl ?? "");
  const [includeDemo, setIncludeDemo] = useState(false);

  const [detail, setDetail] = useState<ErrorGroup | null>(null);
  const [samples, setSamples] = useState<OpsErrorRow[]>([]);
  const [samplesLoading, setSamplesLoading] = useState(false);

  useEffect(() => {
    apiGetJson<TenantPickerOption[] | { tenants: TenantPickerOption[] }>("/api/admin/tenants")
      .then((r) => setTenants(Array.isArray(r) ? r : (r.tenants ?? [])))
      .catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({ grouped: "1", since: presetToSince(since), limit: "100" });
    if (severity !== "all") params.set("severity", severity);
    if (tenantId) params.set("tenantId", tenantId);
    if (q.trim()) params.set("q", q.trim());
    if (includeDemo) params.set("includeDemo", "1");
    return params;
  }, [since, severity, tenantId, q, includeDemo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetJson<{ kpis: Kpis; groups: ErrorGroup[] }>(`/api/ops/errors?${buildParams()}`);
      setKpis(data.kpis);
      setGroups(data.groups);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar incidentes");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { void load(); }, [load]);

  const openDetail = async (g: ErrorGroup) => {
    setDetail(g);
    setSamples([]);
    setSamplesLoading(true);
    try {
      const params = new URLSearchParams({ fingerprint: g.fingerprint, since: presetToSince(since), limit: "10" });
      if (tenantId) params.set("tenantId", tenantId);
      if (includeDemo) params.set("includeDemo", "1");
      const data = await apiGetJson<{ samples: OpsErrorRow[] }>(`/api/ops/errors?${params}`);
      setSamples(data.samples);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar muestras");
    } finally {
      setSamplesLoading(false);
    }
  };

  const filterControls = (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
      <Select value={since} onValueChange={setSince}>
        <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SINCE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={severity} onValueChange={setSeverity}>
        <SelectTrigger className="h-9 text-xs md:w-[140px]"><SelectValue placeholder="Severidad" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas</SelectItem>
          <SelectItem value="error">Crítico</SelectItem>
          <SelectItem value="warn">Warning</SelectItem>
        </SelectContent>
      </Select>
      <TenantPicker tenants={tenants} value={tenantId} onChange={setTenantId} placeholder="Todos los workspaces" className="h-9 w-full md:w-[220px] text-xs" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar mensaje, request_id…" className="h-9 text-xs md:w-[200px]" />
      <button
        type="button"
        onClick={() => setIncludeDemo((v) => !v)}
        className={`h-9 rounded-md border px-3 text-xs font-medium ${includeDemo ? "border-amber-500/50 bg-amber-500/10 text-amber-700" : "text-muted-foreground"}`}
      >
        {includeDemo ? "Incluye demos" : "Sin demos/seed"}
      </button>
      <Button size="sm" onClick={() => void load()} disabled={loading}>Aplicar</Button>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <OpsSubnav hint="Errores agrupados por fingerprint real — no una lista plana de ocurrencias repetidas." />

      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold text-foreground">Errores</h1>
          <p className="text-xs text-muted-foreground">sm_platform_errors — {groups.length} tipo(s) de error distintos</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {kpis ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <KpiTile label="Total (período)" value={kpis.total} />
          <KpiTile label="Críticos" value={kpis.critical} tone="error" />
          <KpiTile label="Warnings" value={kpis.warn} tone="warn" />
          <KpiTile label="Workspaces afectados" value={kpis.distinctTenants} />
          <KpiTile label="Tipos distintos" value={kpis.distinctFingerprints} />
        </div>
      ) : null}

      <div className="hidden md:block">{filterControls}</div>
      <div className="md:hidden">
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <Button size="sm" variant="outline" className="w-full">
              <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" /> Filtros
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
            <SheetHeader><SheetTitle>Filtros</SheetTitle></SheetHeader>
            <div className="mt-3">{filterControls}</div>
          </SheetContent>
        </Sheet>
      </div>

      {groups.length === 0 && !loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Sin errores para estos filtros 🎉</div>
      ) : null}

      {/* Mobile: cards */}
      <div className="flex flex-col gap-2 md:hidden">
        {groups.map((g) => (
          <button key={g.fingerprint} type="button" onClick={() => void openDetail(g)} className="rounded-xl border p-3 text-left hover:bg-muted/40">
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-xs font-medium truncate">{g.event}</span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium border ${SEVERITY_COLOR[g.severity] ?? ""}`}>
                {SEVERITY_LABELS[g.severity] ?? g.severity}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 truncate">{g.sampleMessage}</p>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {g.count} ocurrencia(s) · {g.distinctTenants} workspace(s) · última: {fmt(g.lastSeenAt)}
            </p>
          </button>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground text-left">
            <tr>
              <th className="p-2">Severidad</th>
              <th className="p-2">Error</th>
              <th className="p-2">Ocurrencias</th>
              <th className="p-2">Workspaces</th>
              <th className="p-2">Primera vez</th>
              <th className="p-2">Última vez</th>
              <th className="p-2 text-right">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.fingerprint} className="border-t border-border/50 hover:bg-muted/20 cursor-pointer" onClick={() => void openDetail(g)}>
                <td className="p-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium border ${SEVERITY_COLOR[g.severity] ?? ""}`}>
                    {SEVERITY_LABELS[g.severity] ?? g.severity}
                  </span>
                </td>
                <td className="p-2 max-w-[360px]">
                  <p className="font-mono text-xs font-medium truncate">{g.event}</p>
                  <p className="text-xs text-muted-foreground truncate">{g.sampleMessage}</p>
                </td>
                <td className="p-2 tabular-nums text-xs font-medium">{g.count}</td>
                <td className="p-2 tabular-nums text-xs">{g.distinctTenants}</td>
                <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(g.firstSeenAt)}</td>
                <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(g.lastSeenAt)}</td>
                <td className="p-2 text-right"><Button size="sm" variant="ghost">Ver</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {detail ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-sm">{detail.event}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium border ${SEVERITY_COLOR[detail.severity] ?? ""}`}>
                    {SEVERITY_LABELS[detail.severity] ?? detail.severity}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {detail.count} ocurrencia(s) · {detail.distinctTenants} workspace(s)
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Primera vez: {fmt(detail.firstSeenAt)} · Última vez: {fmt(detail.lastSeenAt)}
                </p>

                <div>
                  <p className="text-xs font-semibold mb-1.5">Muestras reales</p>
                  {samplesLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-4 justify-center">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando…
                    </div>
                  ) : samples.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin muestras para el período/filtro actual.</p>
                  ) : (
                    <ul className="space-y-2">
                      {samples.map((s) => (
                        <li key={s.id} className="text-xs border-t border-border/50 pt-2 first:border-0 first:pt-0">
                          <p className="text-muted-foreground">{fmt(s.createdAt)} · {s.tenantId || "platform"}</p>
                          <p className="mt-0.5">{s.errorMessage}</p>
                          {s.requestId ? (
                            <Link to="/admin/ops/traces" search={{ requestId: s.requestId } as never} className="text-primary hover:underline font-mono text-[11px]">
                              Ver traza ({s.requestId.slice(0, 8)}…)
                            </Link>
                          ) : null}
                          {s.errorStack ? (
                            <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-1.5 text-[10px]">{s.errorStack}</pre>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <p className="text-[11px] text-muted-foreground pt-1 border-t">
                  Sin acción de "marcar resuelto"/"reintentar": el modelo no tiene columna de estado ni un mecanismo de retry seguro genérico — agregarlo requeriría una extensión de backend real, fuera de alcance de este rediseño de UI.
                </p>

                <Link to="/admin" search={{ tab: "workspaces" } as never}>
                  <Button size="sm" variant="outline" className="w-full">Ver workspaces</Button>
                </Link>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
